import { cmsApi } from '../../services/cmsApi';

export interface WebhookData {
    _id: string;
    webhook_id: string;
    webhook_url?: string;
    name: string;
    description?: string;
    auth_type?: 'basic_auth' | 'bearer_token' | 'api_key' | 'none';
    status: 'active' | 'inactive';
    username?: string;
    password?: string;
    bearer_token?: string;
    api_key?: string;
    latest_payload?: any;
    latest_payload_format?: string;
    last_received_at?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface WebhookPayloadData {
    webhook_id: string;
    name?: string;
    status?: string;
    has_payload: boolean;
    source?: 'redis' | 'mongodb';
    format?: string;
    received_at?: string;
    payload?: any;
    raw_payload?: any;
    message?: string;
}

export interface WebhookListResponse {
    code: number;
    message?: string;
    data: WebhookData[];
}

export interface WebhookDetailResponse {
    code: number;
    message?: string;
    data: WebhookData;
}

export interface WebhookPayloadResponse {
    code: number;
    message?: string;
    data: WebhookPayloadData;
}

export interface GenerateWebhookUrlRequest {
    auth_type: 'none' | 'basic_auth' | 'bearer_token' | 'api_key';
    username?: string;
    password?: string;
    bearer_token?: string;
    api_key?: string;
}

export interface GenerateWebhookUrlResponse {
    webhook_url?: string;
    code?: number;
    message?: string;
    data?: {
        webhook_url?: string;
        webhook_id?: string;
    };
}

export interface SaveWebhookRequest {
    name: string;
    description?: string;
    webhook_url: string;
    auth_type: 'none' | 'basic_auth' | 'bearer_token' | 'api_key';
    username?: string;
    password?: string;
    bearer_token?: string;
    api_key?: string;
}

export interface UpdateWebhookRequest {
    id: string;
    name?: string;
    description?: string;
    status?: 'active' | 'inactive';
    auth_type?: 'none' | 'basic_auth' | 'bearer_token' | 'api_key';
    username?: string;
    password?: string;
    bearer_token?: string;
    api_key?: string;
}

export const webhookApi = cmsApi.injectEndpoints({
    endpoints: (builder) => ({
        getWebhooks: builder.query<WebhookListResponse, void>({
            query: () => ({
                url: '/cms/v2/webhooks',
                method: 'GET',
            }),
            providesTags: (result) =>
                result?.data
                    ? [
                        ...result.data.map(({ _id, webhook_id }) => ({
                            type: 'Webhooks' as const,
                            id: _id || webhook_id,
                        })),
                        { type: 'Webhooks', id: 'LIST' },
                    ]
                    : [{ type: 'Webhooks', id: 'LIST' }],
        }),
        getWebhookById: builder.query<WebhookDetailResponse, string>({
            query: (id) => ({
                url: `/cms/v2/webhooks/${id}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, id) => [{ type: 'Webhooks', id }],
        }),
        generateWebhookUrl: builder.mutation<GenerateWebhookUrlResponse, GenerateWebhookUrlRequest>({
            query: (body) => ({
                url: '/cms/v2/webhooks/generate-url',
                method: 'POST',
                body,
            }),
        }),
        saveWebhook: builder.mutation<WebhookDetailResponse, SaveWebhookRequest>({
            query: (body) => ({
                url: '/cms/v2/webhooks',
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Webhooks', id: 'LIST' }],
        }),
        updateWebhook: builder.mutation<WebhookDetailResponse, UpdateWebhookRequest>({
            query: ({ id, ...body }) => ({
                url: `/cms/v2/webhooks/${id}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_res, _err, { id }) => [
                { type: 'Webhooks', id },
                { type: 'Webhooks', id: 'LIST' },
            ],
        }),
        deleteWebhook: builder.mutation<{ code?: number; message?: string }, string>({
            query: (id) => ({
                url: `/cms/v2/webhooks/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Webhooks', id: 'LIST' }],
        }),
    }),
});

export const {
    useGetWebhooksQuery,
    useLazyGetWebhooksQuery,
    useGetWebhookByIdQuery,
    useGenerateWebhookUrlMutation,
    useSaveWebhookMutation,
    useUpdateWebhookMutation,
    useDeleteWebhookMutation,
} = webhookApi;
